<template>
  <div
    class="dialog-form-btns"
    :class="{
      row: !isMobileView,
      column: isMobileView,
      'single-style': onlyNegative,
      'double-style': !onlyNegative,
    }"
  >
    <q-btn
      class="dialog-form-btns__btn dialog-form-btns__btn--negative"
      :class="{
        'q-mr-md': !isMobileView,
        'q-mb-md': isMobileView,
      }"
      :data-testid="negativeDataTestid"
      :label="negativeLabel"
      :disable="negativeDisabled"
      outline
      @click="onNegativeClick"
    />
    <template v-if="!onlyNegative">
      <q-btn
        v-for="(btn, index) in positiveButtons"
        :key="btn.label"
        :label="btn.label"
        :disable="btn.disable"
        :loading="btn.loading"
        :type="btn.type"
        :data-testid="btn.dataTestid"
        outline
        class="dialog-form-btns__btn dialog-form-btns__btn--positive btn-opacity"
        :class="{
          'q-ml-xl': index === 0 && !isMobileView,
          'q-mr-md': !isMobileView,
          'q-mb-md': isMobileView,
        }"
        @click="btn.onClick"
      />
      <q-btn
        class="dialog-form-btns__btn dialog-form-btns__btn--positive btn-opacity"
        :data-testid="positiveDataTestid"
        :type="positiveType"
        :label="positiveLabel"
        :disable="positiveDisabled"
        :loading="loading"
        outline
        @click="onPositiveClick"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

import { useWindowDimensions } from '@/composables/useWindowDimensions';
import { bp600 } from '@/constants/breakpoints';

type Button = {
  label: string;
  disable?: boolean;
  loading?: boolean;
  type?: string;
  dataTestid?: string;
  onClick?(e: Event): void;
};

/*****************************************************************************
 * PROPS
 ****************************************************************************/
withDefaults(
  defineProps<{
    negativeLabel?: string;
    negativeDataTestid?: string;
    positiveLabel?: string;
    positiveDataTestid?: string;
    positiveDisabled?: boolean;
    negativeDisabled?: boolean;
    loading?: boolean;
    onlyNegative?: boolean;
    positiveButtons?: Button[];
    positiveType?: string;
  }>(),
  {
    negativeLabel: '',
    negativeDataTestid: 'dialog-form-btn-negative',
    positiveLabel: '',
    positiveDataTestid: 'dialog-form-btn-positive',
    positiveDisabled: false,
    negativeDisabled: false,
    loading: false,
    onlyNegative: false,
    positiveButtons: undefined,
    positiveType: 'submit',
  },
);

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits(['action:negative', 'action:positive']);

/*****************************************************************************
 * SETUP
 ****************************************************************************/
const onNegativeClick = () => {
  emit('action:negative');
};
const onPositiveClick = () => {
  emit('action:positive');
};

const windowDimensions = useWindowDimensions();
const isMobileView = computed(() => windowDimensions.width < bp600);
</script>

<style lang="scss" scoped>
.dialog-form-btns {
  flex-wrap: nowrap;
  justify-content: flex-end;

  &__btn {
    width: 100%;
    height: 60px;
  }
}

.dialog-form-btns.row.single-style {
  width: 12rem;
}

.dialog-form-btns.row.double-style {
  width: 100%;
}

.dialog-form-btns.column.single-style,
.dialog-form-btns.column.double-style {
  width: 100%;
}
</style>
